import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatNaira, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Save, Plus, FileText, Trash2, User as UserIcon, Briefcase, Coins, IdCard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Field defined at module scope so state updates don't remount inputs (would lose focus)
function HRField({ label, value, onChange, type = "text", full = false, disabled = false }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="mt-1" disabled={disabled} />
    </div>
  );
}

export default function EmployeeProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const targetId = userId || me?.id;
  const isMe = targetId === me?.id;
  const canEditAll = me?.role === "admin" || me?.role === "manager" || isMe;

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({});
  const [salary, setSalary] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [salaryOpen, setSalaryOpen] = useState(false);
  const [salaryForm, setSalaryForm] = useState({ effective_date: new Date().toISOString().slice(0, 10), gross: 0, note: "" });
  const [docOpen, setDocOpen] = useState(false);
  const [docForm, setDocForm] = useState({ type: "contract", filename: "", note: "" });

  const load = () => {
    api.get("/users").then(r => {
      const u = r.data.find(x => x.id === targetId);
      setUser(u);
    });
    api.get(`/hr/hris/${targetId}`).then(r => setProfile(r.data.empty ? {} : r.data)).catch(() => {});
    api.get(`/hr/salary-history/${targetId}`).then(r => setSalary(r.data)).catch(() => {});
    api.get(`/hr/documents/${targetId}`).then(r => setDocuments(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [targetId]);

  const saveProfile = async () => {
    try {
      await api.patch(`/hr/hris/${targetId}`, profile);
      toast.success("HR profile saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addSalary = async () => {
    try {
      await api.post("/hr/salary-history", { ...salaryForm, user_id: targetId, gross: Number(salaryForm.gross) });
      toast.success("Salary entry added");
      setSalaryOpen(false);
      setSalaryForm({ effective_date: new Date().toISOString().slice(0, 10), gross: 0, note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addDocument = async () => {
    if (!docForm.filename) return toast.error("Filename required");
    try {
      await api.post("/hr/documents", { ...docForm, user_id: targetId });
      toast.success("Document added");
      setDocOpen(false);
      setDocForm({ type: "contract", filename: "", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/hr/documents/${id}`);
    load();
  };

  const Field = (props) => <HRField {...props} disabled={!canEditAll} />;

  if (!user) return <div className="text-slate-400 text-sm">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      <div className="kpi-card">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            <AvatarImage src={user.avatar} />
            <AvatarFallback>{user.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="font-heading font-bold text-2xl text-slate-900">{user.name}</div>
            <div className="text-sm text-slate-500">{user.email}</div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Chip variant="aqua">{user.role?.replace("_", " ")}</Chip>
              {user.department && <Chip>{user.department}</Chip>}
              {profile.job_title && <Chip variant="info">{profile.job_title}</Chip>}
            </div>
          </div>
          {canEditAll && (
            <Button onClick={saveProfile} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-1" /> Save profile
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="personal">
        <TabsList>
          <TabsTrigger value="personal" data-testid="tab-personal"><UserIcon className="w-4 h-4 mr-1.5" />Personal</TabsTrigger>
          <TabsTrigger value="employment" data-testid="tab-employment"><Briefcase className="w-4 h-4 mr-1.5" />Employment</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance"><IdCard className="w-4 h-4 mr-1.5" />Compliance & Bank</TabsTrigger>
          {(me?.role === "admin" || me?.role === "manager" || isMe) && (
            <TabsTrigger value="salary" data-testid="tab-salary"><Coins className="w-4 h-4 mr-1.5" />Compensation</TabsTrigger>
          )}
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-4 h-4 mr-1.5" />Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="mt-4">
          <div className="kpi-card grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Date of birth" type="date" value={profile.date_of_birth} onChange={v => setProfile({...profile, date_of_birth: v})} />
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Gender</Label>
              <Select value={profile.gender || ""} onValueChange={v => setProfile({...profile, gender: v})} disabled={!canEditAll}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer_not">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Phone" value={profile.phone} onChange={v => setProfile({...profile, phone: v})} />
            <Field label="Address" full value={profile.address} onChange={v => setProfile({...profile, address: v})} />
            <Field label="Emergency contact name" value={profile.emergency_contact_name} onChange={v => setProfile({...profile, emergency_contact_name: v})} />
            <Field label="Emergency contact phone" value={profile.emergency_contact_phone} onChange={v => setProfile({...profile, emergency_contact_phone: v})} />
            <Field label="Emergency contact relation" value={profile.emergency_contact_relation} onChange={v => setProfile({...profile, emergency_contact_relation: v})} />
            <Field label="Next of kin" value={profile.next_of_kin} onChange={v => setProfile({...profile, next_of_kin: v})} />
          </div>
        </TabsContent>

        <TabsContent value="employment" className="mt-4">
          <div className="kpi-card grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Job title" value={profile.job_title} onChange={v => setProfile({...profile, job_title: v})} />
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Employment type</Label>
              <Select value={profile.employment_type || ""} onValueChange={v => setProfile({...profile, employment_type: v})} disabled={!canEditAll}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full-time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="part_time">Part-time</SelectItem>
                  <SelectItem value="intern">Intern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Hire date" type="date" value={profile.hire_date} onChange={v => setProfile({...profile, hire_date: v})} />
            <Field label="Skills (comma-separated)" value={(profile.skills || []).join(", ")} onChange={v => setProfile({...profile, skills: v.split(",").map(s => s.trim()).filter(Boolean)})} />
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="mt-4">
          <div className="kpi-card grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="NIN (National ID)" value={profile.nin} onChange={v => setProfile({...profile, nin: v})} />
            <Field label="TIN (Tax number)" value={profile.tax_number} onChange={v => setProfile({...profile, tax_number: v})} />
            <Field label="Bank name" value={profile.bank_name} onChange={v => setProfile({...profile, bank_name: v})} />
            <Field label="Bank account #" value={profile.bank_account_number} onChange={v => setProfile({...profile, bank_account_number: v})} />
            <Field label="Pension PFA" value={profile.pension_pfa} onChange={v => setProfile({...profile, pension_pfa: v})} />
            <Field label="Pension PIN" value={profile.pension_pin} onChange={v => setProfile({...profile, pension_pin: v})} />
          </div>
        </TabsContent>

        <TabsContent value="salary" className="mt-4 space-y-4">
          <div className="flex justify-end">
            {(me?.role === "admin" || me?.role === "manager") && (
              <Dialog open={salaryOpen} onOpenChange={setSalaryOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-salary-btn">
                    <Plus className="w-4 h-4 mr-1" /> Salary change
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Record salary change</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Effective date</Label><Input type="date" value={salaryForm.effective_date} onChange={e => setSalaryForm({...salaryForm, effective_date: e.target.value})} /></div>
                    <div><Label>Gross monthly (₦)</Label><Input type="number" value={salaryForm.gross} onChange={e => setSalaryForm({...salaryForm, gross: e.target.value})} /></div>
                    <div><Label>Note</Label><Input value={salaryForm.note} onChange={e => setSalaryForm({...salaryForm, note: e.target.value})} placeholder="Promotion, annual review, etc." /></div>
                  </div>
                  <DialogFooter><Button onClick={addSalary} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-4 py-3">Effective</th>
                  <th className="text-right px-4 py-3">Gross monthly</th>
                  <th className="text-left px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {salary.map(s => (
                  <tr key={s.id} className="row">
                    <td className="px-4 py-3 text-slate-800 num">{s.effective_date}</td>
                    <td className="px-4 py-3 text-right font-heading font-semibold num">{formatNaira(s.gross)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{s.note}</td>
                  </tr>
                ))}
                {salary.length === 0 && <tr><td colSpan={3} className="text-center py-8 text-slate-500 text-sm">No compensation history.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4 space-y-4">
          {(me?.role === "admin" || me?.role === "manager") && (
            <div className="flex justify-end">
              <Dialog open={docOpen} onOpenChange={setDocOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-doc-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add document
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add employee document</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Type</Label>
                      <Select value={docForm.type} onValueChange={v => setDocForm({...docForm, type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="offer">Offer letter</SelectItem>
                          <SelectItem value="id">Government ID</SelectItem>
                          <SelectItem value="nin">NIN slip</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select></div>
                    <div><Label>Filename</Label><Input value={docForm.filename} onChange={e => setDocForm({...docForm, filename: e.target.value})} placeholder="e.g. contract_2026.pdf" /></div>
                    <div><Label>Note</Label><Input value={docForm.note} onChange={e => setDocForm({...docForm, note: e.target.value})} /></div>
                    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                      File upload storage is pending — this stores metadata only for now.
                    </div>
                  </div>
                  <DialogFooter><Button onClick={addDocument} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {documents.map(d => (
              <div key={d.id} className="kpi-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-brand-aqua-light text-brand-aqua-hover flex items-center justify-center">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">{d.filename}</div>
                    <div className="text-xs text-slate-500 capitalize">{d.type} · {formatDate(d.created_at)}</div>
                    {d.note && <div className="text-xs text-slate-600 mt-1">{d.note}</div>}
                  </div>
                  {(me?.role === "admin" || me?.role === "manager") && (
                    <button onClick={() => deleteDocument(d.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
            {documents.length === 0 && <div className="col-span-full text-center py-8 text-slate-500 text-sm">No documents on file.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
